import { Dropdown } from "bootstrap";

/**
 * Row-action menus (the "⋯" button of every table).
 *
 * A menu living inside a table is clipped by the scroll container of that table, and the last
 * rows force the user to scroll to reach the actions. So, while a menu is open:
 *  - it is moved to <body> (no ancestor can clip or re-anchor it);
 *  - Popper is switched off for it (display: "static") and the menu is placed by hand, right
 *    under the "⋯" button, aligned to its right edge (absolute, in document coordinates);
 *  - when there is not enough room below it opens upward; it is always kept inside the viewport
 *    (and scrolls by itself if it is taller than the screen);
 *  - it follows the button when the page or the table scrolls, and closes if the button leaves
 *    the screen.
 * When it closes, it goes back where Vue rendered it.
 */
const GAP = 4;
const MARGIN = 8;

type Origin = { parent: Node; next: Node | null };
const origin = new WeakMap<HTMLElement, Origin>();
let current: { toggle: HTMLElement; menu: HTMLElement } | null = null;
// menus currently living in <body>, with their button
const moved = new Map<HTMLElement, HTMLElement>();

function stopTracking() {
  window.removeEventListener("scroll", onViewportChange, true);
  window.removeEventListener("resize", onViewportChange);
  current = null;
}

/**
 * A menu item that navigates makes Vue remove the whole page, button included, so Bootstrap
 * never sends "hidden" and the menu (moved to <body>) would stay on screen over the next page.
 * Any menu whose button is gone is removed here.
 */
function dropOrphans() {
  moved.forEach((toggle, menu) => {
    if (toggle.isConnected) return;
    moved.delete(menu);
    origin.delete(menu);
    if (current?.menu === menu) stopTracking();
    menu.remove();
  });
}

/** Closes the open row-action menu (called on every route change). */
export function closeFixedDropdowns() {
  if (current) Dropdown.getInstance(current.toggle)?.hide();
  dropOrphans();
}

function findMenu(toggle: HTMLElement): HTMLElement | null {
  const next = toggle.nextElementSibling as HTMLElement | null;
  if (next?.classList.contains("dropdown-menu")) return next;
  return toggle.parentElement?.querySelector(":scope > .dropdown-menu") as HTMLElement | null;
}

// The template stylesheet forces `.dropdown-menu { position: relative !important }` (and another
// !important rule for menus inside <td>), so plain inline styles lose: set them with !important.
const setStyles = (menu: HTMLElement, styles: Record<string, string>) =>
  Object.entries(styles).forEach(([prop, value]) => menu.style.setProperty(prop, value, "important"));

function place(toggle: HTMLElement, menu: HTMLElement) {
  const r = toggle.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // button scrolled out of the screen: nothing to anchor to
  if (r.bottom < 0 || r.top > vh || r.right < 0 || r.left > vw) {
    Dropdown.getInstance(toggle)?.hide();
    return;
  }

  setStyles(menu, { "max-height": `${vh - MARGIN * 2}px` });
  const mw = menu.offsetWidth;
  const mh = menu.offsetHeight;

  const below = vh - r.bottom - GAP - MARGIN;
  const above = r.top - GAP - MARGIN;
  let top: number;
  if (mh <= below || below >= above) top = r.bottom + GAP; // fits below, or more room below than above
  else top = r.top - GAP - mh; // open upward
  top = Math.min(Math.max(top, MARGIN), Math.max(MARGIN, vh - mh - MARGIN));

  let left = r.right - mw; // aligned to the right edge of the button
  left = Math.min(Math.max(left, MARGIN), Math.max(MARGIN, vw - mw - MARGIN));

  // Document coordinates (position: absolute in <body>): independent of any ancestor that would
  // turn position: fixed into something relative to itself, and the menu scrolls with the page.
  setStyles(menu, { top: `${top + window.scrollY}px`, left: `${left + window.scrollX}px` });

  if (localStorage.getItem("debug-dropdown")) {
    console.log("[dropdown]", { button: { top: r.top, bottom: r.bottom, right: r.right }, viewport: { vw, vh }, menu: { mw, mh }, top, left, scroll: { x: window.scrollX, y: window.scrollY } });
  }
}

let raf = 0;
const onViewportChange = (e: Event) => {
  if (!current) return;
  if (e.target instanceof Node && current.menu.contains(e.target)) return; // scrolling inside the menu
  cancelAnimationFrame(raf);
  raf = requestAnimationFrame(() => current && place(current.toggle, current.menu));
};

function onShow(e: Event) {
  const toggle = (e as any).relatedTarget as HTMLElement | undefined;
  const menu = toggle && findMenu(toggle);
  if (!toggle || !menu || origin.has(menu)) return;
  origin.set(menu, { parent: menu.parentNode as Node, next: menu.nextSibling });
  moved.set(menu, toggle);
  document.body.appendChild(menu);
  menu.style.visibility = "hidden"; // shown once placed, no flicker
}

function onShown(e: Event) {
  const toggle = (e as any).relatedTarget as HTMLElement | undefined;
  const menu = toggle && ((Dropdown.getInstance(toggle) as any)?._menu as HTMLElement | undefined);
  if (!toggle || !menu || !origin.has(menu)) return;
  current = { toggle, menu };

  // Whatever instance opened the menu, Popper must not position it: destroying it also resets
  // the inline styles it wrote, so ours are applied afterwards.
  const instance = Dropdown.getInstance(toggle) as any;
  if (instance?._popper) {
    instance._popper.destroy();
    instance._popper = null;
  }
  menu.removeAttribute("style");
  setStyles(menu, {
    position: "absolute",
    inset: "auto",
    margin: "0",
    transform: "none",
    "z-index": "2000",
    width: "max-content",
    "min-width": "11rem",
    "overflow-y": "auto",
    visibility: "hidden",
  });
  place(toggle, menu);
  menu.style.setProperty("visibility", "visible", "important");
  window.addEventListener("scroll", onViewportChange, true);
  window.addEventListener("resize", onViewportChange);
}

function onHidden(e: Event) {
  const toggle = (e as any).relatedTarget as HTMLElement | undefined;
  const menu = toggle && ((Dropdown.getInstance(toggle) as any)?._menu as HTMLElement | undefined);
  const from = menu && origin.get(menu);
  if (!menu || !from) return;

  stopTracking();
  moved.delete(menu);

  menu.removeAttribute("style");
  if (from.parent.isConnected) {
    from.parent.insertBefore(menu, from.next && from.next.parentNode === from.parent ? from.next : null);
  } else {
    menu.remove(); // the row was re-rendered while the menu was open
  }
  origin.delete(menu);
}

const init = () => {
  document.querySelectorAll<HTMLElement>('[data-bs-toggle="dropdown"]').forEach((el) => {
    if (Dropdown.getInstance(el)) return;
    // display "static": Bootstrap does not let Popper move the menu, we place it ourselves
    new Dropdown(el, { display: "static" });
  });
};

// Capture phase: runs before Bootstrap's delegated click handler, so the instance it finds is ours
// (also for toggles rendered after the last scan).
function ensureInstance(e: Event) {
  const toggle = (e.target as HTMLElement | null)?.closest?.('[data-bs-toggle="dropdown"]') as HTMLElement | null;
  if (toggle && !Dropdown.getInstance(toggle)) new Dropdown(toggle, { display: "static" });
}

export function initFixedDropdowns() {
  document.addEventListener("click", ensureInstance, true);
  document.addEventListener("show.bs.dropdown", onShow);
  document.addEventListener("shown.bs.dropdown", onShown);
  document.addEventListener("hidden.bs.dropdown", onHidden);

  init();
  let timer: ReturnType<typeof setTimeout>;
  new MutationObserver(() => {
    dropOrphans(); // immediately: an orphaned menu must not stay over the next page
    clearTimeout(timer);
    timer = setTimeout(init, 80);
  }).observe(document.body, { childList: true, subtree: true });
}
